import type { ReactNode } from "react";
import { Trans } from "react-i18next";
import { useAtom } from "jotai";
import { Spin } from "antd";
import { twMerge } from "tailwind-merge";
import { Button } from "@kalodata/kds-design";
import { ClipModal } from "@components/ClipModal/ClipModal";
import { contactUsModalOpenAtom } from "@src/jotai/contactUs";
import { useSupportContacts } from "./useSupportContacts";
import { CommunityCard } from "./CommunityCard";
import { SocialMediaCard } from "./SocialMediaCard";
import { EmailCard } from "./EmailCard";

const CARD_WIDTH = 205;
const CARD_GAP = 16;

/** 4 张卡片时换成两行两列，其余情况单行居中 */
const isGridLayout = (count: number) => count === 4;

/** 弹窗宽度紧贴内容：列数 * 卡片宽 + 间距 + 左右内边距 */
const getModalWidth = (count: number) => {
  const columns = isGridLayout(count) ? 2 : Math.max(count, 1);
  const paddingX = isGridLayout(count) ? 48 : 24;
  return columns * CARD_WIDTH + (columns - 1) * CARD_GAP + paddingX * 2;
};

export const ContactUsModal = () => {
  const [open, setOpen] = useAtom(contactUsModalOpenAtom);
  const { status, data, refetch } = useSupportContacts(open);

  const close = () => setOpen(false);

  const cards: ReactNode[] = [];
  if (data?.community) cards.push(<CommunityCard key="community" community={data.community} />);
  if (data?.socialMedias?.length) cards.push(<SocialMediaCard key="social" socialMedias={data.socialMedias} />);
  if (data?.email) cards.push(<EmailCard key="email" email={data.email} />);

  const isEmpty = status === "succeeded" && cards.length === 0;
  const grid = isGridLayout(cards.length);

  return (
    <ClipModal
      open={open}
      onCancel={close}
      width={getModalWidth(cards.length)}
      centered
      title={<Trans>Contact Us</Trans>}
      classNames={{
        container: twMerge("pt-[16px]! pb-[40px]! flex flex-col gap-0", grid ? "px-[48px]!" : "px-[24px]!"),
        title: "text-[16px]! font-bold! leading-[22px]! text-kds-fg",
        header: "mb-0!",
        body: "p-0! flex-1 min-h-0",
      }}
    >
      {(status === "idle" || status === "loading") && (
        <div className="flex min-h-[240px] items-center justify-center">
          <Spin />
        </div>
      )}

      {status === "failed" && (
        <div className="flex min-h-[240px] flex-col items-center justify-center gap-[16px]">
          <span className="text-[14px] leading-[20px] text-kds-fg-soft">
            <Trans>Failed to load contact information.</Trans>
          </span>
          <Button variant="outline" size="sm" onClick={refetch}>
            <Trans>Retry</Trans>
          </Button>
        </div>
      )}

      {isEmpty && (
        <div className="flex min-h-[240px] items-center justify-center">
          <span className="text-[14px] leading-[20px] text-kds-fg-soft">
            <Trans>No contact information available.</Trans>
          </span>
        </div>
      )}

      {status === "succeeded" && cards.length > 0 && (
        <div
          className={twMerge(
            "pt-[40px]",
            grid ? "grid grid-cols-2 auto-rows-fr gap-[16px]" : "flex items-stretch justify-center gap-[16px]",
          )}
        >
          {cards}
        </div>
      )}
    </ClipModal>
  );
};
